import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { contactSalesApi } from './api'
import type {
  ContactQueryDetailResponse,
  ContactQueryReply,
  ContactSalesPayload,
} from './types'

const QK = {
  myList: ['contact-sales', 'my-list'] as const,
  myDetail: (id: number) => ['contact-sales', 'my-detail', id] as const,
}

export function useSendContactSales() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ContactSalesPayload) => contactSalesApi.send(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-sales'] }),
  })
}

export function useMyContactQueries() {
  return useQuery({
    queryKey: QK.myList,
    queryFn: () => contactSalesApi.myList(),
    // Refresh the list while the user is on the page so reply counts and
    // last-reply timestamps stay current without a manual refresh. Pauses
    // automatically when the tab is hidden.
    refetchInterval: 30_000,
  })
}

export function useMyContactQuery(id: number | null) {
  return useQuery({
    queryKey: QK.myDetail(id ?? 0),
    queryFn: () => contactSalesApi.myGet(id as number),
    enabled: !!id,
    // Active reading view - poll every 5s so a Bokapsys reply appears
    // without the customer having to refresh. Pauses on hidden tab.
    refetchInterval: 5_000,
  })
}

export function useCloseMyContactQuery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => contactSalesApi.myClose(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: QK.myDetail(id) })
      qc.invalidateQueries({ queryKey: QK.myList })
    },
  })
}

export function useReopenMyContactQuery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => contactSalesApi.myReopen(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: QK.myDetail(id) })
      qc.invalidateQueries({ queryKey: QK.myList })
    },
  })
}

export function useReplyToMyContactQuery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      contactSalesApi.myReply(id, body),
    // Optimistic insert so the bubble shows up the instant the user
    // clicks Send. The negative temp id is replaced when the server
    // round-trip completes (onSettled invalidates and refetches).
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: QK.myDetail(id) })
      const previous = qc.getQueryData<ContactQueryDetailResponse>(QK.myDetail(id))
      const tempReply: ContactQueryReply = {
        id: -Date.now(),
        contact_message_id: id,
        author_user_id: null,
        author_type: 'customer',
        author_name: null,
        author_email: null,
        body,
        created_at: new Date().toISOString(),
      }
      qc.setQueryData<ContactQueryDetailResponse>(QK.myDetail(id), (old) =>
        old ? { ...old, replies: [...old.replies, tempReply] } : old,
      )
      return { previous }
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(QK.myDetail(vars.id), ctx.previous)
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: QK.myDetail(vars.id) })
      qc.invalidateQueries({ queryKey: QK.myList })
    },
  })
}
